<template lang="pug">
transition(name="fade", mode="out-in")
  .pa-4.text-center(v-if="overviewLoading", key="loading")
    v-progress-circular(:size="64", indeterminate)

  v-col.pt-8(v-else, key="content", cols="12")
    v-row.mb-8(no-gutters, justify="space-around")
      v-col(v-for="card in cards", :key="card.key", cols="5")
        v-card(color="#efebe980", style="height: 100%")
          v-card-title
            v-icon.mr-2 {{ card.icon }}
            span.headline {{ card.title }}
          v-card-text
            .display-1.text-center(:class="card.percentColor")
              | {{ card.percent }}%
            .mt-4
              .mt-2.d-flex.justify-space-between(
                v-for="item in card.items",
                :key="item.key"
              )
                .subtitle-1 {{ item.label }}
                .subtitle-1.ml-2 {{ item.value }}
    .mb-12
      v-progress-linear(
        :height="32",
        rounded,
        background-color="#e0f2f1",
        color="cyan darken-2",
        :value="progress"
      )
        template(v-slot="{ value }")
          b {{ value }}%
    template(v-if="overviewData")
      v-row.mb-4(v-for="el in progresses", :key="el.key", no-gutters)
        v-card(style="width: 100%")
          v-card-text
            v-row(align="center", no-gutters)
              v-col(md="2")
                v-progress-circular.mr-4(
                  :rotate="-90",
                  :size="100",
                  :width="10",
                  :color="el.percentColor",
                  :value="el.percent"
                )
                  h1.black--text {{ el.percent }}%
              v-col(md="10")
                .d-flex.justify-space-between.align-center
                  h2.mr-4 {{ el.title }}
                  .d-flex.flex-column
                    component(v-if="el.comp", v-bind="el.comp")
                    v-btn(
                      v-if="el.btn && !el.btn.hidden",
                      width="290",
                      color="primary",
                      :disabled="el.btn.disabled",
                      @click="el.btn.action"
                    ) {{ el.btn.label }}

    OverviewDocumentsModal(
      v-model="documentsModal",
      :caseID="ID",
      @success="onSuccess"
    )
    OverviewLicenceModal(
      v-model="licenceModal",
      :caseID="ID",
      @success="onSuccess"
    )
</template>

<script>
import { mapState, mapGetters } from "vuex";
import AdditionalQuestions from "./components/AdditionalQuestions";
import OverviewDocumentsModal from "./components/OverviewDocumentsModal";
import OverviewLicenceModal from "./components/OverviewLicenceModal";

export default {
  name: "employer-case-overview",

  components: {
    OverviewDocumentsModal,
    OverviewLicenceModal,
  },

  data: () => ({
    documentsModal: false,
    licenceModal: false,
  }),

  mounted() {
    this.$store.dispatch("usercase/sponsorship/refreshOverview");
  },

  methods: {
    percentColor(percent, forText) {
      let color = "teal";
      let modifier = "lighten-1";
      if (percent < 50) {
        color = "red";
        modifier = "lighten-1";
      }
      if (percent < 99) {
        color = "orange";
        modifier = "darken-1";
      }
      return forText
        ? color + "--text" + " " + "text--" + modifier
        : color + " " + modifier;
    },

    changeTab(i) {
      document.documentElement.scrollTo({
        top: 0,
        behavior: "smooth",
      });
      setTimeout(() => {
        this.$emit("goto", i);
      }, 500);
    },

    onSuccess() {
      window.location.reload();
    },
  },

  computed: {
    ...mapState("usercase/sponsorship", {
      overviewData: (state) => state.overview,
      overviewLoading: (state) => state.loading.overview,
    }),

    ...mapGetters("usercase", ["currentUsercase", "ID", "statusIndex"]),

    eligibility() {
      const eligibility = this.overviewData?.eligibility;
      return {
        icon: "mdi-account-check",
        title: "Eligibility",
        items: [
          {
            label: "Genuine business",
            value: eligibility?.genuine_business,
          },
          {
            label: "Supporting documents",
            value: eligibility?.supporting_docs,
          },
          {
            label: "Operations in the UK",
            value: eligibility?.operations_in_the_uk.Bool,
          },
        ],
        percent: eligibility?.percent,
      };
    },

    suitability() {
      const suitability = this.overviewData?.suitability;
      return {
        icon: "mdi-account-cog",
        title: "Suitability",
        items: [
          {
            label: "HR system in place",
            value: suitability?.hr_system_in_place,
          },
          {
            label: "Facilitation of visits and checks",
            value: suitability?.visits_and_checks,
          },
          {
            label: "Genuine vacancy",
            value: suitability?.genuine_vacancy,
          },
          {
            label: "Key personnel",
            value: suitability?.key_personnel,
          },
          {
            label: "No previous non-compliance",
            value: suitability?.previous_non_compliance,
          },
          {
            label: "Absence of criminal convictions",
            value: suitability?.criminal_conviction,
          },
        ],
        percent: suitability?.percent,
      };
    },

    cards() {
      return [this.eligibility, this.suitability].map(
        ({ icon, title, items, percent }, i) => {
          const cardKey = "card:" + i;
          return {
            key: cardKey,
            icon,
            title,
            items: items.map(({ label, value }, j) => {
              const itemKey = cardKey + "/item:" + j;
              return {
                key: itemKey,
                label,
                value: value ? "Yes" : "No",
              };
            }),
            percent: Math.floor(percent ?? 0),
            percentColor: this.percentColor(percent, true),
          };
        }
      );
    },

    progress() {
      return Math.floor(
        this.progresses.reduce((sum, { percent }) => sum + percent, 0) /
          this.progresses.length
      );
    },

    progresses() {
      const payable = this.cards.every(({ percent }) => percent === 100);
      const isPaid = this.overviewData?.payment ?? false;
      const company_questions = this.overviewData?.company_questions ?? 0;
      const case_questions = this.overviewData?.case_questions ?? 0;
      const documents_uploaded = this.overviewData?.documents_uploaded ?? 0;
      const candidate_questions = this.overviewData?.candidate_questions ?? 0;
      return [
        {
          percent: company_questions,
          title: "Complete company profile",
          comp: {
            is: AdditionalQuestions,
            group: "company",
            isPaid,
            disabled: !payable && !isPaid,
          },
          btn: {
            hidden: company_questions !== 1,
            label: "view",
            action: () => {
              this.$router.push("/company");
            },
          },
        },

        {
          percent: case_questions,
          title: "Complete case profile",
          comp: {
            is: AdditionalQuestions,
            group: "case",
            isPaid,
            disabled: !payable && !isPaid,
          },
          btn: {
            hidden: case_questions !== 1,
            label: "view",
            action: () => {
              this.changeTab(2);
            },
          },
        },

        {
          percent: isPaid ? 1 : 0,
          title: "Pay for service",
          btn: {
            label: isPaid ? "paid" : "pay",
            disabled: !payable || isPaid,
            action: () => {
              if (!isPaid) {
                this.$store.commit("setPayDialog", true);
              }
            },
          },
        },

        {
          percent: candidate_questions,
          title: "Provide candidate details",
          btn: {
            label: candidate_questions < 1 ? "go" : "view",
            action: () => {
              this.changeTab(3);
            },
          },
        },

        {
          percent: documents_uploaded,
          title: "Upload required documents",
          btn: {
            label: documents_uploaded < 1 ? "upload" : "view",
            disabled: !isPaid,
            action: () => {
              this.changeTab(4);
            },
          },
        },

        {
          percent: this.statusIndex >= 5 ? 1 : 0,
          title: "Submit case for expert review",
          btn: {
            label: this.statusIndex >= 5 ? "view" : "go",
            disabled: {
              condition: "previous-undone",
              exclusions: [3],
            },
            action: () => {
              this.changeTab(5);
            },
          },
        },

        {
          percent: this.statusIndex < 9 ? 0 : this.statusIndex === 9 ? 0.5 : 1,
          title: "Review and submit online application to the Home Office",
          btn: {
            label: this.statusIndex >= 10 ? "view" : "go",
            disabled: this.statusIndex < 9,
            action: () => {
              this.changeTab(5);
            },
          },
        },

        {
          percent: this.statusIndex >= 11 ? 1 : 0,
          title: "Confirm sending documents to the Home Office",
          btn: {
            label: this.statusIndex >= 11 ? "view" : "confirm",
            disabled: {
              condition: "previous-undone",
              exclusions: [3],
            },
            action: () => {
              if (this.statusIndex >= 11) {
                this.changeTab(5);
              } else {
                this.documentsModal = true;
              }
            },
          },
        },

        {
          percent: this.statusIndex >= 12 ? 1 : 0,
          title: "Notify us about the Home Office decision",
          btn: {
            label: this.statusIndex >= 12 ? "view" : "confirm",
            disabled: {
              condition: "previous-undone",
              exclusions: [3],
            },
            action: () => {
              if (this.statusIndex >= 12) {
                this.changeTab(5);
              } else {
                this.licenceModal = true;
              }
            },
          },
        },
      ].map((el, i, arr) => {
        const percent = Math.floor(el.percent * 100);
        const btn = el.btn;
        const condition = btn.disabled?.condition;
        const exclusions = btn.disabled?.exclusions ?? [];
        if (condition === "previous-undone") {
          btn.disabled = arr
            .slice(0, i)
            .some((item, j) => !exclusions.includes(j) && item.percent !== 1);
        }
        return {
          key: "progress:" + i,
          percent,
          percentColor: this.percentColor(percent),
          title: el.title,
          comp: el?.comp ?? null,
          btn,
        };
      });
    },
  },
};
</script>